/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import * as React from 'react';

import { InvoiceModel } from '../helpers/invoice-data';
import { IInvoicesListResources } from '../invoices-list.props.autogenerated';

interface IRequestInvoiceModalProps {
    className: string;
    email: string;
    isModalOpen: boolean;
    submitRequestLabel: string;
    modalHeaderLabel: string;
    sendToEmailLabel: string;
    cancelLabel: string;
    searchPlaceholderLabel: string;
    invoices: InvoiceModel[];
    invoicesFoundString: string;
    singleInvoiceFoundString: string;
    searchButtonAltText: string;
    resources: IInvoicesListResources;
    toggle(): void;
    onSubmitRequest(invoiceId: string): void;
}

/**
 * On submit request click function.
 * @param selectedInvoiceIndex -Check is busy.
 * @param onClosing -On closing function.
 * @param filteredInvoices -Invoice modal.
 * @param onSubmitRequest -On submit request function.
 * @returns Call on submit request.
 */
const onSubmitRequestClick = (selectedInvoiceIndex: number, onClosing: () => void,
    filteredInvoices: InvoiceModel[], onSubmitRequest: (invoiceId: string) => void) => () => {
    const defaultIndex = 0;
    if (selectedInvoiceIndex >= defaultIndex) {
        onClosing();
        onSubmitRequest(filteredInvoices[selectedInvoiceIndex].id);
    }
};

/**
 * On search input change function.
 * @param setSearchInput -Set search input.
 * @returns Set search input function.
 */
const searchInputChangedHandler = (setSearchInput: React.Dispatch<React.SetStateAction<string>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
};

/**
 * Search entered handler.
 * @param invoices -InvoiceModel.
 * @param searchInput -String.
 * @param setFilteredInvoices -InvoiceModel action.
 * @param filteredInvoices -Filtered Invoices.
 * @param setSelectedInvoiceIndex -Selected Invoice Index.
 * @param setShouldShowSearchResults -Show search result action.
 * @param setInvoiceOptions -Invoice options action.
 * @param selectedInvoiceIndex -Selected invoice index.
 * @param className -String.
 * @returns Set invoice options.
 */
const searchEnteredHandler = (invoices: InvoiceModel[], searchInput: string,
    setFilteredInvoices: React.Dispatch<React.SetStateAction<InvoiceModel[]>>,
    filteredInvoices: InvoiceModel[],
    setSelectedInvoiceIndex: React.Dispatch<React.SetStateAction<number>>,
    setShouldShowSearchResults: React.Dispatch<React.SetStateAction<boolean>>,
    setInvoiceOptions: React.Dispatch<React.SetStateAction<JSX.Element>>,
    selectedInvoiceIndex: number,
    className: string) => () => {
    let updatedInvoices = filteredInvoices;
    if (!StringExtensions.isNullOrEmpty(searchInput)) {
        updatedInvoices = invoices.filter((invoice) => invoice.id.includes(searchInput));
    }

    setFilteredInvoices(updatedInvoices);

    if (ArrayExtensions.hasElements(updatedInvoices)) {
        setSelectedInvoiceIndex(0);
    } else {
        setSelectedInvoiceIndex(-1);
    }
    setShouldShowSearchResults(true);

    setInvoiceOptions(renderInvoiceOptions(updatedInvoices, setSelectedInvoiceIndex, className, selectedInvoiceIndex));
};

/**
 * On cancel function.
 * @param onClosing -Closing function.
 * @param toggle -Toggle Function.
 * @returns Toggle function.
 */
const onCancelHandler = (onClosing: () => void, toggle: () => void) => () => {
    onClosing();
    toggle();
};

/**
 * On invoice select.
 * @param setSelectedInvoiceIndex -Set Selected Invoice Index.
 * @param index -Number.
 * @returns Set Selected Invoice Index.
 */
const onInvoiceSelectHandler = (setSelectedInvoiceIndex: (newIndex: number) => void, index: number) => () => {
    setSelectedInvoiceIndex(index);
};

const renderInvoiceOptions = (filteredInvoices: InvoiceModel[], setSelectedInvoiceIndex: (newIndex: number) => void, className: string, selectedInvoiceIndex: number) => {
    return (
        <div>
            {filteredInvoices.map((invoice, index) => {
                const onInvoiceSelect = onInvoiceSelectHandler(setSelectedInvoiceIndex, index);

                const radioButtonId = `invoice__${invoice.id}`;

                return (
                    <div key={index} className={`${className}__requestInvoice__filteredInvoiceResult`}>
                        <input
                            type='radio' id={radioButtonId} name='invoiceSelect'
                            value={invoice.id} key={invoice.id} checked={index === selectedInvoiceIndex}
                            aria-checked={index === selectedInvoiceIndex} onChange={onInvoiceSelect} />
                        <label htmlFor={radioButtonId} className={`${className}__requestInvoice__selectedInvoiceButton`}>
                            {invoice.id}
                        </label>
                    </div>
                );
            })}
        </div>
    );
};

export const RequestInvoiceModal: React.FC<IRequestInvoiceModalProps> = ({ className, email, isModalOpen, toggle, modalHeaderLabel, sendToEmailLabel, submitRequestLabel, cancelLabel, onSubmitRequest, invoices, searchPlaceholderLabel,
    invoicesFoundString, singleInvoiceFoundString, searchButtonAltText, resources }) => {
    const [selectedInvoiceIndex, setSelectedInvoiceIndex] = React.useState<number>(-1);
    const [filteredInvoices, setFilteredInvoices] = React.useState<InvoiceModel[]>(invoices);
    const [searchInput, setSearchInput] = React.useState<string>('');
    const [shouldShowSearchResults, setShouldShowSearchResults] = React.useState<boolean>(false);
    const focusDelay = 110;
    const invoiceOptionsData = renderInvoiceOptions(filteredInvoices, setSelectedInvoiceIndex, className, selectedInvoiceIndex);
    const [invoiceOptions, setInvoiceOptions] = React.useState<JSX.Element>(invoiceOptionsData);
    const focusElement = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        if (isModalOpen && focusElement.current) {
            setTimeout(() => {
                focusElement.current?.focus();
            }, focusDelay);
        }
    }, [isModalOpen]);

    /**
     * Sets the filtered invoices to the ones that were selected on the main screen.
     */
    const setFilteredInvoicesToSelectedInvoices = () => {
        setSelectedInvoiceIndex(-1);
        const selectedInvoices = invoices.filter(invoice => invoice.isSelected);
        if (ArrayExtensions.hasElements(selectedInvoices)) {
            setFilteredInvoices(selectedInvoices);

            // If only one invoice selected, auto select it
            if (selectedInvoices.length === 1) {
                setSelectedInvoiceIndex(0);
            }

            setInvoiceOptions(renderInvoiceOptions(selectedInvoices, setSelectedInvoiceIndex, className, 0));
        } else {
            setFilteredInvoices(invoices);
            setInvoiceOptions(renderInvoiceOptions(invoices, setSelectedInvoiceIndex, className, selectedInvoiceIndex));
        }
    };

    const searchEntered = searchEnteredHandler(invoices,
        searchInput, setFilteredInvoices, filteredInvoices,
        setSelectedInvoiceIndex, setShouldShowSearchResults,
        setInvoiceOptions, selectedInvoiceIndex, className);

    const onClosing = () => {
        setSelectedInvoiceIndex(-1);
        setFilteredInvoices(invoices);
        setSearchInput('');
        setShouldShowSearchResults(false);
    };

    /**
     * Sets the invoices that are possible to select when the dialog is opened.
     */
    function onOpened() {
        setSearchInput('');
        setFilteredInvoicesToSelectedInvoices();
    }

    const searchResultString = filteredInvoices.length === 1 ? singleInvoiceFoundString : invoicesFoundString;
    const invoicesFoundReplaceIndex = searchResultString.indexOf('{0}');

    return (
        <Modal
            className={classnames('msc-invoices-list_requestInvoice__modal', 'msc-modal-input-required')}
            isOpen={isModalOpen}
            horizontalPosition='center'
            verticalPosition='center'
            autoFocus
            fade={false}
            toggle={onCancelHandler(onClosing, toggle)}
            onOpened={onOpened}
            aria-label={resources.requestInvoiceDialogAriaLabel}
        >
            <ModalHeader tag='h2' toggle={onCancelHandler(onClosing, toggle)}>
                {modalHeaderLabel}
            </ModalHeader>
            <ModalBody>
                <div className={`${className}__requestInvoice__modalBody`}>
                    <label htmlFor='sendToEmail'>
                        {sendToEmailLabel}
                    </label>
                    <div id='sendToEmail' className={`${className}__requestInvoice__sendToEmail`}>
                        {email}
                    </div>
                    <div>
                        {shouldShowSearchResults &&
                        <div className={`${className}__requestInvoice__searchResultDisplay`} aria-live='assertive' role='alert'>
                            <span>
                                {searchResultString.substring(0, invoicesFoundReplaceIndex)}
                            </span>
                            <span className={`${className}__requestInvoice__searchResultNumber`}>
                                {filteredInvoices.length}
                            </span>
                            <span>
                                {searchResultString.substring(invoicesFoundReplaceIndex + 3)}
                            </span>
                        </div>}
                        <div className={`${className}__requestInvoice__search`}>
                            <input
                                ref={focusElement}
                                type='text' id='searchBar' className={`${className}__requestInvoice__searchBar`}
                                placeholder={searchPlaceholderLabel} onChange={searchInputChangedHandler(setSearchInput)} />
                            <button className={`${className}__requestInvoice__searchButton`} onClick={searchEntered} title={searchButtonAltText} />
                        </div>
                        {invoiceOptions}
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <button
                    className={`${className}__requestInvoice__submitRequest`}
                    onClick={onSubmitRequestClick(selectedInvoiceIndex, onClosing, filteredInvoices, onSubmitRequest)}>
                    {submitRequestLabel}
                </button>
                <button className={`${className}__requestInvoice__cancelRequest`} onClick={onCancelHandler(onClosing, toggle)}>
                    {cancelLabel}
                </button>
            </ModalFooter>
        </Modal>
    );
};
